import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertTriangle, Check, Clock, DollarSign, FileText, RefreshCw, Upload, Sparkles } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useCurrentBusiness } from "@/hooks/use-current-business";
import { format } from "date-fns";

// ─────────────────────────────────────────────────────────────────────
// AI Auto-Reconciliation Engine — deterministic heuristic, runs client-side.
// Returns per-transaction match decisions with confidence + human reason.
// (No LLM call — keeps the page fast and works offline.)
// ─────────────────────────────────────────────────────────────────────
type AiMatch = {
  id: number;
  matched: boolean;
  confidence: number;
  reason: string;
};

type AiSuggestion = {
  strategy: "all" | "partial" | "manual" | "balanced";
  matches: AiMatch[];
  matchedIds: Set<number>;
  summary: string;
  autoApply: boolean; // true ⇒ safe to clear without user confirmation
  delta: number;      // remaining gap after applying suggestion
};

type Cadence = "weekly" | "biweekly" | "monthly" | "irregular";

type AiExplanation = {
  reason: string;
  factors: {
    occurrences: number;
    avgAmount: number;
    cadence: Cadence;
    recencyDays: number;
  };
};

type ReconciliationPattern = {
  id: number;
  businessId: number;
  vendorName: string;
  canonicalVendor?: string;
  avgAmount: number;
  stdAmount: number;
  frequencyDays: number;
  occurrences: number;
  confidence: number;
  weightedConfidence?: number;
  cadence?: Cadence;
  nextExpectedDate?: string | null;
  daysUntilExpected?: number | null;
  isOverdue?: boolean;
  tentative?: boolean;
  lastSeen: string;
  explanation?: AiExplanation;
};

type Prediction = {
  expectedTransactions: Array<{
    canonicalVendor: string;
    expectedAmount: number;
    nextExpectedDate: string;
    daysUntilExpected: number;
    cadence: Cadence;
    confidence: number;
    tentative?: boolean;
  }>;
  missingTransactions: Array<{
    canonicalVendor: string;
    expectedAmount: number;
    expectedDate: string;
    daysOverdue: number;
    cadence: Cadence;
    confidence: number;
    tentative?: boolean;
  }>;
  confidence: number;
};

type AnomalyResult = {
  anomaly: boolean;
  reason: string;
  patternConfidence?: number;
};

type BusinessInsights = {
  recurringIncome: Array<{
    canonicalVendor: string;
    avgAmount: number;
    cadence: Cadence;
    occurrences: number;
    monthlyEstimate: number;
    monthlyEstimateReliable?: boolean;
    monthlyEstimateLabel?: string;
    confidenceLabel?: string;
    effectiveConfidence: number;
    tentative?: boolean;
  }>;
  recurringExpenses: Array<{
    canonicalVendor: string;
    avgAmount: number;
    cadence: Cadence;
    occurrences: number;
    monthlyEstimate: number;
    monthlyEstimateReliable?: boolean;
    monthlyEstimateLabel?: string;
    confidenceLabel?: string;
    effectiveConfidence: number;
    tentative?: boolean;
  }>;
  anomalies: Array<{
    transactionId: number;
    description: string;
    amount: number;
    date: string;
    expectedAmount: number;
    deviationPct: number;
    canonicalVendor: string;
  }>;
  predictions: Prediction;
  totals: {
    monthlyIncomeEstimate: number;
    monthlyExpenseEstimate: number;
    netMonthlyEstimate: number;
    patternCount: number;
    canonicalCount: number;
    avgEffectiveConfidence: number;
  };
  narrative?: Array<{
    priority: 1 | 2 | 3;
    category: "risk" | "trend" | "info";
    message: string;
    detail?: string;
  }>;
  health?: {
    status: "stable" | "warning" | "volatile";
    summary: string;
  };
};

// Mirror server-side extractVendorKey — keeps client + server lookups aligned.
function extractVendorKey(t: any): string {
  const raw = t?.vendor_name || t?.vendorName || t?.payee || t?.description || "";
  if (!raw) return "";
  return String(raw)
    .toUpperCase()
    .trim()
    .replace(/[#0-9\-:./]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .substring(0, 80);
}

function detectAnomaly(t: any, patternMap: Map<string, ReconciliationPattern>): AnomalyResult | null {
  const key = extractVendorKey(t);
  const pattern = patternMap.get(key);
  if (!pattern || pattern.occurrences < 2 || !pattern.avgAmount) return null;
  const abs = Math.abs(parseFloat(t.amount || "0"));
  if (!Number.isFinite(abs) || abs <= 0) return null;
  const deviation = Math.abs(abs - pattern.avgAmount) / pattern.avgAmount;
  if (deviation > 0.30) {
    return {
      anomaly: true,
      reason: `Amount $${abs.toFixed(2)} is ${(deviation * 100).toFixed(0)}% off typical $${pattern.avgAmount.toFixed(2)}`,
      patternConfidence: pattern.confidence,
    };
  }
  return null;
}

function signedAmount(t: any): number {
  const n = parseFloat(t.amount);
  if (Number.isNaN(n)) return 0;
  return (t.type === "income" || t.type === "deposit") ? n : -n;
}

function autoReconcile(
  uncleared: any[],
  beginning: number,
  bankStatement: number,
  alreadyClearedSum: number,
  patternMap: Map<string, ReconciliationPattern> = new Map(),
): AiSuggestion {
  const target = bankStatement - beginning - alreadyClearedSum;
  const epsilon = 0.01;

  // Edge: nothing to do.
  if (uncleared.length === 0) {
    return {
      strategy: Math.abs(target) < epsilon ? "balanced" : "manual",
      matches: [],
      matchedIds: new Set(),
      summary: Math.abs(target) < epsilon
        ? "Already balanced — nothing left to clear."
        : `No uncleared transactions, but the books are off by ${formatUSD(target)}. Investigate manually.`,
      autoApply: false,
      delta: target,
    };
  }

  const total = uncleared.reduce((s, t) => s + signedAmount(t), 0);

  // Strategy 1 — totals match exactly. Safe to auto-apply.
  // Confidence per row gets a small boost when the vendor matches a learned
  // pattern, so the UI can surface "AI learned this pattern" badges.
  if (Math.abs(total - target) < epsilon) {
    return {
      strategy: "all",
      matches: uncleared.map((t) => {
        const key = extractVendorKey(t);
        const pat = patternMap.get(key);
        const baseConfidence = 0.95;
        const boosted = pat
          ? Math.min(0.99, baseConfidence + (pat.confidence - 0.5) * 0.1)
          : baseConfidence;
        return {
          id: t.id,
          matched: true,
          confidence: boosted,
          reason: pat
            ? `Total matches; ${pat.vendorName} is a known recurring vendor (${pat.occurrences}× seen)`
            : "Total matches bank statement exactly",
        };
      }),
      matchedIds: new Set(uncleared.map((t: any) => t.id)),
      summary: `Exact match — all ${uncleared.length} transactions sum to the bank target (${formatUSD(target)}).`,
      autoApply: true,
      delta: 0,
    };
  }

  // Strategy 2 — bounded subset-sum (max 5 items), pattern-prioritized.
  // Exhaustive over the top-N pattern-weighted candidates with branch-and-bound
  // pruning. Bounded so even 30+ uncleared rows complete in milliseconds.
  // Falls through to the greedy strategy if no exact subset of size ≤ 5 hits.
  {
    const MAX_SUBSET = 5;
    const CANDIDATE_POOL = 18; // exhaust over top-18 pattern-ranked items only
    const candidates = uncleared
      .map((t: any) => {
        const key = extractVendorKey(t);
        const pat = patternMap.get(key);
        return {
          t,
          amt: signedAmount(t),
          patternScore: pat ? pat.confidence * Math.log1p(pat.occurrences) : 0,
          pattern: pat,
        };
      })
      .sort((a, b) => {
        if (a.patternScore !== b.patternScore) return b.patternScore - a.patternScore;
        return Math.abs(b.amt) - Math.abs(a.amt);
      })
      .slice(0, CANDIDATE_POOL);

    let best: { picked: typeof candidates; score: number } | null = null;
    const search = (start: number, picked: typeof candidates, sum: number) => {
      if (best && Math.abs(sum - target) < epsilon && picked.length === best.picked.length) return;
      if (Math.abs(sum - target) < epsilon && picked.length > 0) {
        // Score: prefer fewer items, then more pattern-backed items.
        const patternHits = picked.reduce((s, x) => s + (x.pattern ? 1 : 0), 0);
        const score = -picked.length * 100 + patternHits;
        if (!best || score > best.score) best = { picked: [...picked], score };
        return;
      }
      if (picked.length >= MAX_SUBSET) return;
      if (start >= candidates.length) return;
      // Branch-and-bound: if even taking the largest remaining can't reach target, prune.
      const remainingMax = candidates.slice(start).reduce((s, x) => s + Math.abs(x.amt), 0);
      if (Math.abs(target - sum) > remainingMax + epsilon) return;
      for (let i = start; i < candidates.length; i++) {
        picked.push(candidates[i]);
        search(i + 1, picked, sum + candidates[i].amt);
        picked.pop();
      }
    };
    search(0, [], 0);

    if (best && (best as { picked: typeof candidates; score: number }).picked.length > 0) {
      const winner = best as { picked: typeof candidates; score: number };
      const matchedIds = new Set(winner.picked.map((p) => p.t.id));
      const lookup = (id: number) => winner.picked.find((p) => p.t.id === id);
      const patternHits = winner.picked.filter((p) => p.pattern).length;
      return {
        strategy: "partial",
        matches: uncleared.map((t: any) => {
          if (!matchedIds.has(t.id)) {
            return { id: t.id, matched: false, confidence: 0, reason: "Not in best-fit subset" };
          }
          const p = lookup(t.id);
          const isPattern = !!p?.pattern;
          const wConf = (p?.pattern as any)?.weightedConfidence ?? p?.pattern?.confidence ?? 0.5;
          const confidence = isPattern
            ? Math.min(0.95, 0.85 + (wConf - 0.5) * 0.1)
            : 0.85;
          const reason = isPattern
            ? `Subset match + ${p!.pattern!.canonicalVendor || p!.pattern!.vendorName} pattern (${p!.pattern!.occurrences}× seen, ${(wConf * 100).toFixed(0)}% confidence)`
            : "Bounded subset (≤5) — sum matches bank target exactly";
          return { id: t.id, matched: true, confidence, reason };
        }),
        matchedIds,
        summary: `Found an exact ${winner.picked.length}-transaction subset that sums to the bank target${patternHits > 0 ? ` (${patternHits} matched a known recurring vendor)` : ""}. Review and Apply.`,
        autoApply: false,
        delta: 0,
      };
    }
  }

  // Strategy 3 — pattern-aware greedy best-fit subset (fallback).
  //   1. Known recurring vendors first (sorted by pattern confidence × occurrences),
  //   2. Then everything else by abs amount desc.
  // Picks each only if it brings runningSum closer to target.
  const scored = uncleared.map((t: any) => {
    const key = extractVendorKey(t);
    const pat = patternMap.get(key);
    return {
      t,
      amt: signedAmount(t),
      patternScore: pat ? pat.confidence * Math.log1p(pat.occurrences) : 0,
      pattern: pat,
    };
  });
  const indexed = scored.sort((a, b) => {
    // Patterns first, then by absolute amount.
    if (a.patternScore !== b.patternScore) return b.patternScore - a.patternScore;
    return Math.abs(b.amt) - Math.abs(a.amt);
  });

  const picked: typeof indexed = [];
  let runningSum = 0;
  for (const item of indexed) {
    const before = Math.abs(runningSum - target);
    const after = Math.abs(runningSum + item.amt - target);
    if (after < before) {
      picked.push(item);
      runningSum += item.amt;
      if (Math.abs(runningSum - target) < epsilon) break;
    }
  }

  if (Math.abs(runningSum - target) < epsilon && picked.length > 0) {
    const matchedIds = new Set(picked.map((p) => p.t.id));
    const pickedScore = (id: number) => picked.find((p) => p.t.id === id);
    return {
      strategy: "partial",
      matches: uncleared.map((t: any) => {
        if (!matchedIds.has(t.id)) {
          return { id: t.id, matched: false, confidence: 0, reason: "Not in best-fit subset" };
        }
        const p = pickedScore(t.id);
        const isPattern = !!p?.pattern;
        const confidence = isPattern
          ? Math.min(0.95, 0.85 + (p!.pattern!.confidence - 0.5) * 0.1)
          : 0.85;
        const reason = isPattern
          ? `Best-fit + ${p!.pattern!.vendorName} pattern (${p!.pattern!.occurrences}× seen, ${(p!.pattern!.confidence * 100).toFixed(0)}% confidence)`
          : "Best-fit subset — sum matches bank target";
        return { id: t.id, matched: true, confidence, reason };
      }),
      matchedIds,
      summary: `Found a subset of ${picked.length} of ${uncleared.length} transactions that sums to the bank target. Review and Apply.`,
      autoApply: false,
      delta: 0,
    };
  }

  // Strategy 4 — no clean match. Surface the gap; user works manually.
  return {
    strategy: "manual",
    matches: uncleared.map((t: any) => ({
      id: t.id,
      matched: false,
      confidence: 0,
      reason: "No automatic match",
    })),
    matchedIds: new Set(),
    summary: `Difference of ${formatUSD(total - target)} couldn't be auto-matched. Pick the right transactions manually.`,
    autoApply: false,
    delta: total - target,
  };
}

function formatUSD(n: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

interface ReconciliationData {
  reconciliation: any;
  clearedTransactions: any[];
  unclearedTransactions: any[];
  discrepancies: any[];
  balances: {
    beginning: number;
    bankStatement: number;
    bookBalance: number;
    difference: number;
  };
}

interface ReconciliationAccount {
  id: number;
  name: string;
  type: string;
}

interface ReconciliationHistoryItem {
  id: number;
  startDate: string;
  endDate: string;
  endingBalance?: string | null;
  status: string;
  reconciledAt?: string | null;
}

export default function BankReconciliation() {
  const { currentBusiness } = useCurrentBusiness();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [selectedAccount, setSelectedAccount] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [bankStatementBalance, setBankStatementBalance] = useState("");
  const [beginningBalance, setBeginningBalance] = useState("");
  // ── ?id= URL load (synchronous initial state) ────────────────────
  // Read ?id= via lazy useState init so the very first render already
  // has the resumed id. Doing this in a useEffect causes a one-frame
  // flicker where the "start new" form paints before state updates,
  // which is what made Resume look like it didn't work.
  const urlReconciliationIdInitial = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const idStr = params.get("id");
      if (!idStr) return null;
      const idNum = parseInt(idStr, 10);
      return Number.isFinite(idNum) && idNum > 0 ? idNum : null;
    } catch {
      return null;
    }
  })();
  const [currentReconciliationId, setCurrentReconciliationId] = useState<number | null>(
    urlReconciliationIdInitial,
  );
  const [selectedTransactions, setSelectedTransactions] = useState<Set<number>>(new Set());
  // Editable balance row (active reconciliation panel)
  const [editBeginning, setEditBeginning] = useState("");
  const [editBankStatement, setEditBankStatement] = useState("");
  // One-shot guard: don't auto-apply on every render / refetch.
  const autoAppliedRef = useRef<number | null>(null);

  // Fetch bank accounts
  const { data: accounts, isLoading: accountsLoading } = useQuery<ReconciliationAccount[]>({
    queryKey: [`/api/chart-of-accounts/${currentBusiness?.id}/accounts`],
    enabled: !!currentBusiness?.id,
  });

  // Fetch current reconciliation data
  const { data: reconciliationData, isLoading: reconciliationLoading } = useQuery<ReconciliationData>({
    queryKey: [`/api/reconciliations/${currentReconciliationId}`],
    enabled: !!currentReconciliationId,
  });

  // Fetch reconciliation history
  const { data: reconciliationHistory } = useQuery<ReconciliationHistoryItem[]>({
    queryKey: [`/api/businesses/${currentBusiness?.id}/accounts/${selectedAccount}/reconciliations`],
    enabled: !!currentBusiness?.id && !!selectedAccount,
  });

  // Start reconciliation mutation. The backend now de-duplicates by
  // business + account + period, so a re-submit returns the existing
  // row instead of creating a duplicate. We surface that with a
  // "Resumed existing reconciliation" toast when reused === true.
  const startReconciliationMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest("POST", `/api/businesses/${currentBusiness?.id}/reconciliations`, data);
      return response.json();
    },
    onSuccess: (data) => {
      setCurrentReconciliationId(data.id);
      toast({
        title: data?.reused ? "Resumed existing reconciliation" : "Reconciliation Started",
        description: data?.reused
          ? "We found an in-progress reconciliation for this period and reopened it."
          : "Bank reconciliation has been initiated successfully.",
      });
      queryClient.invalidateQueries({ queryKey: [`/api/reconciliations/${data.id}`] });
      queryClient.invalidateQueries({
        queryKey: [`/api/businesses/${currentBusiness?.id}/accounts/${selectedAccount}/reconciliations`],
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to start reconciliation",
        variant: "destructive",
      });
    },
  });

  // ── Reconciliation lifecycle handlers (Resume / Complete / Delete) ───
  // All use existing endpoints. apiRequest carries credentials and
  // throws on non-2xx so error toasts stay consistent.
  const handleResume = (id: number) => {
    setCurrentReconciliationId(id);
    // Reflect the resumed id in the URL so a refresh keeps the user where
    // they were. Uses history.replaceState (no wouter remount).
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("id", String(id));
      window.history.replaceState({}, "", url.toString());
    } catch { /* non-fatal */ }
    queryClient.invalidateQueries({ queryKey: [`/api/reconciliations/${id}`] });
    toast({ title: "Resumed", description: "Loaded the selected reconciliation." });
  };

  const handleComplete = async (id: number) => {
    try {
      await apiRequest("POST", `/api/reconciliations/${id}/complete`, {});
      toast({ title: "Reconciliation completed", description: "The period is now locked." });
      queryClient.invalidateQueries();
    } catch (err: any) {
      toast({
        title: "Could not complete",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    }
  };

  // Strips ?id= from the URL — used whenever we transition back to the
  // "no active reconciliation" view so the URL stays consistent with state.
  const clearReconciliationIdFromUrl = () => {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has("id")) {
        url.searchParams.delete("id");
        window.history.replaceState({}, "", url.toString());
      }
    } catch { /* non-fatal */ }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this reconciliation? This cannot be undone.")) return;
    try {
      await apiRequest("DELETE", `/api/reconciliations/${id}`);
      toast({ title: "Reconciliation deleted" });
      if (currentReconciliationId === id) {
        setCurrentReconciliationId(null);
        clearReconciliationIdFromUrl();
      }
      queryClient.invalidateQueries();
    } catch (err: any) {
      toast({
        title: "Could not delete",
        description: err?.message || "Completed reconciliations are locked.",
        variant: "destructive",
      });
    }
  };

  // (Initial ?id= load is now done synchronously above via lazy useState.)

  // Clear transactions mutation
  const clearTransactionsMutation = useMutation({
    mutationFn: async (transactionIds: number[]) => {
      const response = await apiRequest("POST", `/api/reconciliations/${currentReconciliationId}/clear-transactions`, {
        transactionIds,
      });
      return response.json();
    },
    onSuccess: () => {
      setSelectedTransactions(new Set());
      toast({
        title: "Transactions Cleared",
        description: "Selected transactions have been marked as cleared.",
      });
      queryClient.invalidateQueries({ queryKey: [`/api/reconciliations/${currentReconciliationId}`] });
    },
  });

  // Auto-match: mark every visible (uncleared) transaction as cleared in
  // one shot. Reuses the existing /clear-transactions endpoint.
  const autoMatchAllMutation = useMutation({
    mutationFn: async () => {
      const ids = (reconciliationData?.unclearedTransactions || []).map((t: any) => t.id);
      if (ids.length === 0) {
        return { cleared: 0 };
      }
      const response = await apiRequest("POST", `/api/reconciliations/${currentReconciliationId}/clear-transactions`, {
        transactionIds: ids,
      });
      const data = await response.json();
      return { cleared: ids.length, ...data };
    },
    onSuccess: (data: any) => {
      toast({
        title: data.cleared > 0 ? "Auto-Match complete" : "Nothing to match",
        description: data.cleared > 0
          ? `Marked ${data.cleared} transaction${data.cleared === 1 ? "" : "s"} as cleared.`
          : "All visible transactions are already cleared.",
      });
      setSelectedTransactions(new Set());
      queryClient.invalidateQueries({ queryKey: [`/api/reconciliations/${currentReconciliationId}`] });
    },
    onError: (err: any) => {
      toast({ title: "Auto-Match failed", description: err?.message || "Please try again.", variant: "destructive" });
    },
  });

  // Update beginning + bank-statement balance on the active reconciliation.
  const updateBalancesMutation = useMutation({
    mutationFn: async (payload: { beginningBalance?: string; bankStatementBalance?: string }) => {
      const response = await apiRequest("PATCH", `/api/reconciliations/${currentReconciliationId}`, payload);
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Balances updated" });
      queryClient.invalidateQueries({ queryKey: [`/api/reconciliations/${currentReconciliationId}`] });
    },
    onError: (err: any) => {
      toast({ title: "Update failed", description: err?.message || "Please try again.", variant: "destructive" });
    },
  });

  // Complete reconciliation mutation
  const completeReconciliationMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/reconciliations/${currentReconciliationId}/complete`);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Reconciliation Complete",
        description: "Bank reconciliation has been completed successfully.",
      });
      setCurrentReconciliationId(null);
      clearReconciliationIdFromUrl();
      queryClient.invalidateQueries();
    },
  });

  // Seed the editable balance fields whenever reconciliation data loads
  // (or the user switches between reconciliations).
  useEffect(() => {
    if (reconciliationData?.balances) {
      setEditBeginning(String(reconciliationData.balances.beginning ?? ""));
      setEditBankStatement(String(reconciliationData.balances.bankStatement ?? ""));
    }
  }, [reconciliationData?.balances?.beginning, reconciliationData?.balances?.bankStatement]);

  // ── AI pattern memory (per-business, refetched when business changes) ─
  const { data: patternsResp } = useQuery<{ patterns: ReconciliationPattern[] }>({
    queryKey: [`/api/businesses/${currentBusiness?.id}/reconciliation-patterns`],
    enabled: !!currentBusiness?.id,
    staleTime: 60_000,
  });
  const patternMap = useMemo(() => {
    const m = new Map<string, ReconciliationPattern>();
    for (const p of patternsResp?.patterns || []) m.set(p.vendorName, p);
    return m;
  }, [patternsResp?.patterns]);

  // ── Predictive intelligence (expected this week + missing/overdue) ───
  const { data: predictions } = useQuery<Prediction>({
    queryKey: [`/api/businesses/${currentBusiness?.id}/reconciliation-predictions`],
    enabled: !!currentBusiness?.id,
    staleTime: 60_000,
  });

  // ── AI Business Insights (recurring income/expenses + monthly est + anomalies) ─
  const { data: insights } = useQuery<BusinessInsights>({
    queryKey: [`/api/businesses/${currentBusiness?.id}/reconciliation-insights`],
    enabled: !!currentBusiness?.id,
    staleTime: 5 * 60_000,
  });

  // ── Client-friendly UX layer (additive — no calculation changes) ─────
  // Soften technical phrasing in any displayed string.
  const softenText = (s: string | undefined): string => {
    if (!s) return "";
    return s
      .replace(/\bnegative cashflow\b/gi, "expenses exceed income")
      .replace(/\bvolatile\b/gi, "")
      .replace(/\banomalies\b/gi, "unusual transactions")
      .replace(/\banomaly\b/gi, "unusual transaction")
      .replace(/\s{2,}/g, " ")
      .trim();
  };

  // Append impact context based on raw deviation percentage.
  const impactPhraseFor = (deviationPctAbs: number | null | undefined): string => {
    if (deviationPctAbs == null) return "";
    if (deviationPctAbs >= 100) return " (significantly higher than usual)";
    if (deviationPctAbs > 0) return " (slightly different than usual)";
    return "";
  };

  // Build dynamic health description from current narrative counts.
  // Tone per UX spec: "You have X things to check before closing".
  const buildDynamicHealthDescription = (
    riskCount: number,
    trendCount: number,
    fallback: string,
  ): string => {
    let lead: string;
    if (riskCount === 0 && trendCount === 0) {
      lead = "Everything looks clear — nothing to check before closing this period.";
    } else if (riskCount === 0) {
      lead = `Your books are on track — ${trendCount} trend${trendCount === 1 ? "" : "s"} to keep an eye on.`;
    } else {
      const noun = riskCount === 1 ? "thing" : "things";
      const trail = trendCount > 0
        ? ` Plus ${trendCount} trend${trendCount === 1 ? "" : "s"} worth a look.`
        : "";
      lead = `You have ${riskCount} ${noun} to check before closing this period.${trail}`;
    }
    return fallback ? `${lead} ${fallback}` : lead;
  };

  // Health card text override per spec: never show "volatile" to clients.
  // Description is computed dynamically further down once we have narrative counts.
  const displayedHealth = useMemo(() => {
    if (!insights?.health) return null;
    const status = insights.health.status;
    const titleText =
      status === "volatile" ? "Review recommended"
        : status === "warning" ? "A few items to review"
        : "Books look healthy";
    return { titleText, rawStatus: status, fallbackSummary: softenText(insights.health.summary) };
  }, [insights?.health]);

  // Detect insight category from message text — drives the action buttons.
  type InsightKind = "missing" | "unusual" | "expected_this_week" | "cashflow" | "other";
  const detectInsightKind = (n: { category: string; message: string }): InsightKind => {
    const m = n.message.toLowerCase();
    if (n.category === "risk" && (m.includes("haven't arrived") || m.includes("missing") || m.includes("not been detected"))) return "missing";
    if (n.category === "risk" && m.includes("unusual")) return "unusual";
    if (n.category === "trend" && m.includes("expected this week")) return "expected_this_week";
    if (n.category === "trend" && (m.includes("exceed") || m.includes("cashflow"))) return "cashflow";
    return "other";
  };

  // Rewrite narrative items: soften, suppress unreliable totals, append impact,
  // detect "high impact" rows for stronger visual weight.
  const displayedNarrative = useMemo(() => {
    if (!insights?.narrative) return [];
    const expectedHasUnreliable = !!predictions?.expectedTransactions?.some(
      (p) => p.tentative || (p.confidence ?? 1) < 0.5,
    );
    return insights.narrative.map((n, i) => {
      const kind = detectInsightKind(n);
      let message = softenText(n.message);
      let detail = softenText(n.detail);
      let deviationPctAbs: number | null = null;
      let highImpact = false;

      // Suppress unreliable totals in "expected this week".
      if (kind === "expected_this_week" && expectedHasUnreliable) {
        detail = "Based on past activity — exact amount still being learned.";
      }

      // Impact context + high-impact flag for unusual-transaction details.
      if (kind === "unusual") {
        const m = /by\s+(\d+)%/.exec(detail);
        if (m) {
          deviationPctAbs = parseInt(m[1], 10);
          if (deviationPctAbs >= 100 && !/high impact/.test(detail)) {
            highImpact = true;
            // Step 6: explicit "(high impact)" label for large deviations.
            const phrase = impactPhraseFor(deviationPctAbs);
            detail = `${detail}${phrase} (high impact)`;
          } else if (!/significantly higher|slightly different/.test(detail)) {
            detail = `${detail}${impactPhraseFor(deviationPctAbs)}`;
          }
        }
      }
      // Missing transactions also escalate when overdue amount is large.
      if (kind === "missing") {
        const dollar = /~\$([\d,]+(?:\.\d+)?)/.exec(detail);
        if (dollar && parseFloat(dollar[1].replace(/,/g, "")) >= 1000) {
          highImpact = true;
          if (!/high impact/.test(detail)) detail = `${detail} (high impact)`;
        }
      }

      // Content-based stable id (djb2 hash of message+detail). Survives
      // re-orderings since it doesn't depend on array index.
      const hashSrc = `${n.category}|${kind}|${message}|${detail || ""}`;
      let h = 5381;
      for (let k = 0; k < hashSrc.length; k++) h = ((h << 5) + h) + hashSrc.charCodeAt(k);
      const stableId = `${n.category}-${Math.abs(h).toString(36)}`;
      return { ...n, message, detail, kind, deviationPctAbs, highImpact, stableId };
    });
  }, [insights?.narrative, predictions?.expectedTransactions]);

  // Resolved-insight state — Map<stableId, { ts, undoable }>. The
  // undoable flag flips false 5s after resolve, hiding the Undo button.
  type ResolvedEntry = { ts: number; undoable: boolean };
  const [resolvedInsights, setResolvedInsights] = useState<Map<string, ResolvedEntry>>(new Map());
  const undoTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Tear down any pending undo timers on unmount.
  useEffect(() => () => {
    undoTimers.current.forEach((t) => clearTimeout(t));
    undoTimers.current.clear();
  }, []);

  const undoResolve = (stableId: string) => {
    const t = undoTimers.current.get(stableId);
    if (t) clearTimeout(t);
    undoTimers.current.delete(stableId);
    setResolvedInsights((prev) => {
      const next = new Map(prev);
      next.delete(stableId);
      return next;
    });
    console.log(`[BankReconciliation] action=undo_resolve`, { stableId });
  };

  const handleAction = (action: string, payload?: any) => {
    console.log(`[BankReconciliation] action=${action}`, payload || "");
    const stableId = payload?.stableId;
    if (!stableId) return;
    setResolvedInsights((prev) => {
      const next = new Map(prev);
      next.set(stableId, { ts: Date.now(), undoable: true });
      return next;
    });
    // 5-second Undo window.
    const existing = undoTimers.current.get(stableId);
    if (existing) clearTimeout(existing);
    const t = setTimeout(() => {
      setResolvedInsights((prev) => {
        const cur = prev.get(stableId);
        if (!cur) return prev;
        const next = new Map(prev);
        next.set(stableId, { ...cur, undoable: false });
        return next;
      });
      undoTimers.current.delete(stableId);
    }, 5000);
    undoTimers.current.set(stableId, t);
  };

  // Friendly badge labels per spec.
  const badgeLabelFor = (n: { category: string }) =>
    n.category === "risk" ? "Check this"
      : n.category === "trend" ? "Insight"
      : "FYI";

  // Translate a raw confidence number into a human, non-numeric phrase.
  const humanConfidenceLabel = (c: number | null | undefined): string => {
    if (c == null) return "Based on recent activity";
    if (c < 0.4) return "Based on limited history";
    if (c < 0.7) return "Based on recent activity";
    return "Based on consistent pattern";
  };

  // ── AI auto-reconciliation engine ───────────────────────────────────
  const aiSuggestion: AiSuggestion | null = useMemo(() => {
    if (!reconciliationData) return null;
    // Already-cleared sum (so the engine knows what's left to attribute).
    const alreadyClearedSum = (reconciliationData.clearedTransactions || [])
      .reduce((s: number, t: any) => s + signedAmount(t), 0);
    return autoReconcile(
      reconciliationData.unclearedTransactions || [],
      Number(reconciliationData.balances?.beginning ?? 0),
      Number(reconciliationData.balances?.bankStatement ?? 0),
      alreadyClearedSum,
      patternMap
    );
  }, [reconciliationData, patternMap]);

  // Per-row anomaly map: { transactionId → AnomalyResult }. Built once.
  const anomalyMap = useMemo(() => {
    const m = new Map<number, AnomalyResult>();
    if (!reconciliationData) return m;
    const all = [
      ...(reconciliationData.unclearedTransactions || []),
      ...(reconciliationData.clearedTransactions || []),
    ];
    for (const t of all) {
      const a = detectAnomaly(t, patternMap);
      if (a?.anomaly) m.set(t.id, a);
    }
    return m;
  }, [reconciliationData, patternMap]);

  // ── Auto-apply ONLY when totals match exactly. One-shot per reconciliation,
  //    only when status is still 'pending' (not completed). Does NOT auto-
  //    complete the reconciliation — the user reviews and clicks Complete.
  //    Auto-clearing is reversible by un-checking; auto-completing is not.
  useEffect(() => {
    if (!aiSuggestion || !aiSuggestion.autoApply) return;
    if (!currentReconciliationId) return;
    if (autoAppliedRef.current === currentReconciliationId) return;
    if (reconciliationData?.reconciliation?.status === "completed") return;
    if (aiSuggestion.matchedIds.size === 0) return;

    autoAppliedRef.current = currentReconciliationId;
    clearTransactionsMutation.mutate(Array.from(aiSuggestion.matchedIds), {
      onSuccess: () => {
        toast({
          title: "AI auto-reconciled",
          description: `Cleared ${aiSuggestion.matchedIds.size} transactions — totals match the bank statement. Review and click Complete to lock the period.`,
        });
      },
    });
  }, [aiSuggestion, currentReconciliationId, reconciliationData?.reconciliation?.status]);

  const handleStartReconciliation = () => {
    // Hard guard: when the page is loaded with ?id=, never auto-create
    // a new reconciliation — we're resuming an existing one. The user
    // must clear the URL (or finish/delete the resumed one first) to
    // start a fresh reconciliation.
    if (urlReconciliationIdInitial && currentReconciliationId === urlReconciliationIdInitial) {
      toast({
        title: "Already viewing this reconciliation",
        description: "You're resuming an existing reconciliation. Complete or delete it before starting a new one.",
      });
      return;
    }

    if (!selectedAccount || !startDate || !endDate || !bankStatementBalance) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields to start reconciliation.",
        variant: "destructive",
      });
      return;
    }

    // Pre-create guard: if a non-completed reconciliation already exists
    // for this exact account+period, give the user a Resume / Start Over
    // chooser instead of silently relying on the backend dedup.
    const sameDay = (a: any, b: any) => new Date(a).toDateString() === new Date(b).toDateString();
    const existing = (reconciliationHistory as any[] | undefined)?.find((r: any) =>
      r.status !== "completed"
      && String(r.accountId) === String(selectedAccount)
      && sameDay(r.startDate, startDate)
      && sameDay(r.endDate, endDate),
    );
    if (existing) {
      const choice = window.confirm(
        "You already have a reconciliation for this period.\n\n" +
        "OK = Resume the existing one\n" +
        "Cancel = Delete it and start over",
      );
      if (choice) {
        handleResume(existing.id);
        return;
      }
      // Start Over: delete the old row, then create fresh.
      (async () => {
        try {
          await apiRequest("DELETE", `/api/reconciliations/${existing.id}`);
        } catch (err: any) {
          toast({
            title: "Could not start over",
            description: err?.message || "Please try again.",
            variant: "destructive",
          });
          return;
        }
        startReconciliationMutation.mutate({
          accountId: selectedAccount,
          startDate,
          endDate,
          bankStatementBalance,
          ...(beginningBalance !== "" ? { beginningBalance } : {}),
        });
      })();
      return;
    }

    startReconciliationMutation.mutate({
      accountId: selectedAccount,
      startDate,
      endDate,
      bankStatementBalance,
      // Optional — backend falls back to last completed reconciliation's
      // ending balance, then 0, if user leaves this blank.
      ...(beginningBalance !== "" ? { beginningBalance } : {}),
    });
  };

  const handleTransactionSelection = (transactionId: number, checked: boolean) => {
    const newSelected = new Set(selectedTransactions);
    if (checked) {
      newSelected.add(transactionId);
    } else {
      newSelected.delete(transactionId);
    }
    setSelectedTransactions(newSelected);
  };

  const handleBulkClear = () => {
    if (selectedTransactions.size === 0) {
      toast({
        title: "No Transactions Selected",
        description: "Please select transactions to clear.",
        variant: "destructive",
      });
      return;
    }
    clearTransactionsMutation.mutate(Array.from(selectedTransactions));
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return <Badge variant="default" className="bg-green-500"><Check className="w-3 h-3 mr-1" />Completed</Badge>;
      case "pending":
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case "requires_review":
        return <Badge variant="destructive"><AlertTriangle className="w-3 h-3 mr-1" />Requires Review</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (accountsLoading) {
    return <div className="flex items-center justify-center h-64">Loading accounts...</div>;
  }

  return (
    <Layout>
      <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Bank Reconciliation</h1>
          <p className="text-muted-foreground">
            Reconcile your book transactions with bank statements
          </p>
        </div>
        <Button variant="outline" size="sm">
          <FileText className="w-4 h-4 mr-2" />
          Export Report
        </Button>
      </div>

      {!currentReconciliationId ? (
        <Card>
          <CardHeader>
            <CardTitle>Start New Reconciliation</CardTitle>
            <CardDescription>
              Select an account and date range to begin the reconciliation process
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label htmlFor="account">Bank Account</Label>
                <Select value={selectedAccount} onValueChange={setSelectedAccount}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts?.map((account: any) => (
                      <SelectItem key={account.id} value={account.id.toString()}>
                        {account.name} ({account.type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="beginningBalance">Beginning Balance</Label>
                <Input
                  id="beginningBalance"
                  type="number"
                  step="0.01"
                  placeholder="Auto from last close"
                  value={beginningBalance}
                  onChange={(e) => setBeginningBalance(e.target.value)}
                />
                <p className="text-[11px] text-muted-foreground">
                  Leave blank to carry forward from the last completed reconciliation.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bankBalance">Bank Statement Balance</Label>
                <Input
                  id="bankBalance"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={bankStatementBalance}
                  onChange={(e) => setBankStatementBalance(e.target.value)}
                />
              </div>
            </div>
            
            <Button 
              onClick={handleStartReconciliation}
              disabled={startReconciliationMutation.isPending}
              className="w-full"
            >
              {startReconciliationMutation.isPending ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <DollarSign className="w-4 h-4 mr-2" />
              )}
              Start Reconciliation
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Confidence trend strip — shows how many patterns the AI has
              learned + average confidence + most-recent learning event. */}
          {/* Business Health — title from displayedHealth, description
              dynamically composed from current narrative counts (excludes
              already-resolved items so the message reflects what's left). */}
          {displayedHealth && (() => {
            const liveItems = displayedNarrative.filter((n) => !resolvedInsights.has(n.stableId));
            const riskCount = liveItems.filter((n) => n.priority === 1).length;
            const trendCount = liveItems.filter((n) => n.priority === 2).length;
            const dynamicDescription = buildDynamicHealthDescription(
              riskCount,
              trendCount,
              displayedHealth.fallbackSummary,
            );
            return (
              <Card className={
                displayedHealth.rawStatus === "stable"
                  ? "border-emerald-200 bg-emerald-50/40"
                  : displayedHealth.rawStatus === "warning"
                    ? "border-amber-200 bg-amber-50/40"
                    : "border-rose-200 bg-rose-50/40"
              }>
                <CardContent className="py-3 flex items-start gap-3">
                  {displayedHealth.rawStatus === "stable" ? (
                    <Check className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                  ) : (
                    <AlertTriangle className={
                      "h-5 w-5 mt-0.5 shrink-0 " + (
                        displayedHealth.rawStatus === "warning" ? "text-amber-600" : "text-rose-600"
                      )
                    } />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={
                        "text-sm font-semibold tracking-wide " + (
                          displayedHealth.rawStatus === "stable" ? "text-emerald-900"
                            : displayedHealth.rawStatus === "warning" ? "text-amber-900"
                            : "text-rose-900"
                        )
                      }>
                        {displayedHealth.titleText}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{dynamicDescription}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* AI Insights — ranked narrative items with action buttons +
              local resolution state. */}
          {displayedNarrative && displayedNarrative.length > 0 && (
            <Card className="border-indigo-200 bg-indigo-50/40">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-indigo-600" />
                  AI Insights
                  <Badge variant="outline" className="text-[10px] ml-auto">
                    {displayedNarrative.filter((n) => n.priority === 1).length} needs review · {displayedNarrative.filter((n) => n.priority === 2).length} insight · {displayedNarrative.filter((n) => n.priority === 3).length} fyi
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {displayedNarrative.map((n) => {
                    const resolvedEntry = resolvedInsights.get(n.stableId);
                    const isResolved = !!resolvedEntry;
                    const showUndo = !!resolvedEntry?.undoable;
                    // Step 2 — high-impact uses amber tones, not rose.
                    const baseClass = n.category === "risk"
                      ? (n.highImpact
                          ? "bg-amber-50 border-amber-400 ring-1 ring-amber-300"
                          : "bg-rose-50 border-rose-200")
                      : n.category === "trend"
                        ? "bg-blue-50 border-blue-200"
                        : "bg-white/60 border-indigo-100";
                    // Step 6 — show humanConfidenceLabel as muted footnote
                    // (uses the overall avg effective confidence as the proxy
                    //  for "how sure is the AI of this insight").
                    const conf = humanConfidenceLabel(insights?.totals?.avgEffectiveConfidence);
                    return (
                      <li
                        key={n.stableId}
                        className={
                          "text-xs flex flex-col gap-2 p-2 rounded border transition-all duration-300 ease-out " +
                          baseClass +
                          (isResolved ? " opacity-60 scale-[0.99]" : "")
                        }
                      >
                        <div className="flex items-start gap-2">
                          <Badge
                            variant="outline"
                            className={
                              "text-[9px] uppercase shrink-0 " +
                              (n.category === "risk"
                                ? (n.highImpact ? "border-amber-500 text-amber-800" : "border-rose-400 text-rose-700")
                                : n.category === "trend"
                                  ? "border-blue-400 text-blue-700"
                                  : "border-indigo-300 text-indigo-700")
                            }
                          >
                            {badgeLabelFor(n)}
                          </Badge>
                          <div className="min-w-0 flex-1">
                            <div className={
                              "font-medium " + (
                                n.category === "risk"
                                  ? (n.highImpact ? "text-amber-900" : "text-rose-900")
                                  : n.category === "trend" ? "text-blue-900" : "text-indigo-900"
                              )
                            }>
                              {n.message}
                              {n.highImpact && !isResolved && (
                                <Badge variant="outline" className="ml-2 text-[9px] uppercase border-amber-500 text-amber-800 bg-amber-100">
                                  High impact
                                </Badge>
                              )}
                            </div>
                            {n.detail && (
                              <div className="text-[11px] text-muted-foreground mt-0.5">{n.detail}</div>
                            )}
                            {/* Step 6 — confidence footnote */}
                            {!isResolved && (
                              <div className="text-[10px] text-muted-foreground/80 mt-1 italic">
                                {conf}
                              </div>
                            )}
                          </div>
                          {isResolved && (
                            <span className="text-[10px] uppercase font-semibold text-emerald-700 shrink-0 flex items-center gap-1 transition-opacity duration-300">
                              <Check className="h-3 w-3" />
                              Resolved just now
                            </span>
                          )}
                        </div>

                        {/* Action buttons — hidden once resolved. */}
                        {!isResolved && n.kind === "missing" && (
                          <div className="flex gap-1.5 flex-wrap pl-1">
                            <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]"
                              onClick={() => handleAction("review_missing", { message: n.message, stableId: n.stableId })}>
                              Review transactions
                            </Button>
                            <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]"
                              onClick={() => handleAction("mark_as_expected", { message: n.message, stableId: n.stableId })}>
                              Mark as expected
                            </Button>
                            <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-muted-foreground"
                              onClick={() => handleAction("dismiss_reminder", { message: n.message, stableId: n.stableId })}>
                              Dismiss reminder
                            </Button>
                          </div>
                        )}
                        {!isResolved && n.kind === "unusual" && (
                          <div className="flex gap-1.5 flex-wrap pl-1">
                            <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]"
                              onClick={() => handleAction("review_unusual", { message: n.message, stableId: n.stableId })}>
                              Review transaction
                            </Button>
                            <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]"
                              onClick={() => handleAction("confirm_valid", { message: n.message, stableId: n.stableId })}>
                              Confirm it's valid
                            </Button>
                            <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-muted-foreground"
                              onClick={() => handleAction("ignore_anomaly", { message: n.message, stableId: n.stableId })}>
                              Ignore anomaly
                            </Button>
                          </div>
                        )}
                        {!isResolved && n.kind === "expected_this_week" && (
                          <div className="flex gap-1.5 flex-wrap pl-1">
                            <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]"
                              onClick={() => handleAction("view_expected", { message: n.message, stableId: n.stableId })}>
                              View transactions
                            </Button>
                          </div>
                        )}

                        {/* Step 5 — Undo (visible 5s after resolve) */}
                        {showUndo && (
                          <div className="flex justify-end pl-1 animate-in fade-in duration-200">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 px-2 text-[10px] text-emerald-700 hover:text-emerald-900"
                              onClick={() => undoResolve(n.stableId)}
                            >
                              Undo
                            </Button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Predictive intelligence — expected this week + missing/overdue.
              Read-only signals; the user still drives clearing. */}
          {predictions && (predictions.expectedTransactions.length > 0 || predictions.missingTransactions.length > 0) && (
            <Card className="border-blue-200 bg-blue-50/40">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-blue-600" />
                  Predicted activity
                  {predictions.confidence > 0 && (
                    <Badge variant="outline" className="text-[10px] ml-auto">
                      {humanConfidenceLabel(predictions.confidence)}
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription className="text-xs">
                  Based on learned vendor cadence (weekly/biweekly/monthly).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {predictions.expectedTransactions.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-blue-900 mb-1">
                      Expected this week ({predictions.expectedTransactions.length})
                    </div>
                    <ul className="space-y-1">
                      {predictions.expectedTransactions.slice(0, 6).map((p, i) => (
                        <li
                          key={`exp-${i}`}
                          className="group text-xs flex items-center justify-between gap-2 py-1 px-2 rounded bg-white/60 border border-blue-100 hover:bg-blue-50/60 transition-colors"
                          title={p.tentative ? "Possible — single observation only, confidence will rise after the next match" : undefined}
                        >
                          <span className="font-medium truncate flex items-center gap-1">
                            {p.canonicalVendor}
                            {/* "Possible" badge hidden by default; shown on row hover. */}
                            {p.tentative && (
                              <Badge
                                variant="outline"
                                className="text-[8px] uppercase border-blue-300 text-blue-700 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                Possible
                              </Badge>
                            )}
                          </span>
                          <span className="flex items-center gap-2 shrink-0">
                            <Badge variant="secondary" className="text-[9px] uppercase">{p.cadence}</Badge>
                            <span className="text-muted-foreground">~{formatUSD(p.expectedAmount)}</span>
                            <span className="text-blue-700">
                              {p.daysUntilExpected === 0 ? "today" : `in ${p.daysUntilExpected}d`}
                            </span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {predictions.missingTransactions.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-amber-900 mb-1 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Missing / overdue ({predictions.missingTransactions.length})
                    </div>
                    <ul className="space-y-1">
                      {predictions.missingTransactions.slice(0, 6).map((p, i) => (
                        <li
                          key={`miss-${i}`}
                          className="group text-xs flex items-center justify-between gap-2 py-1 px-2 rounded bg-amber-50 border border-amber-200 hover:bg-amber-100/50 transition-colors"
                          title={p.tentative ? "Possible — single observation only, confidence will rise after the next match" : undefined}
                        >
                          <span className="font-medium truncate flex items-center gap-1">
                            {p.canonicalVendor}
                            {p.tentative && (
                              <Badge
                                variant="outline"
                                className="text-[8px] uppercase border-amber-400 text-amber-700 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                Possible
                              </Badge>
                            )}
                          </span>
                          <span className="flex items-center gap-2 shrink-0">
                            <Badge variant="secondary" className="text-[9px] uppercase">{p.cadence}</Badge>
                            <span className="text-muted-foreground">~{formatUSD(p.expectedAmount)}</span>
                            <span className="text-amber-700">{p.daysOverdue}d overdue</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* AI Insights — recurring income/expenses, monthly estimate, anomalies */}
          {insights && insights.totals.patternCount > 0 && (
            <Card className="border-violet-200 bg-gradient-to-br from-violet-50/60 to-indigo-50/40">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-violet-600" />
                  AI Insights
                  <Badge variant="outline" className="text-[10px] ml-auto">
                    {insights.totals.canonicalCount} vendors · {insights.totals.patternCount} patterns
                  </Badge>
                </CardTitle>
                <CardDescription className="text-xs">
                  Recurring income, expenses, and recent anomalies the AI has identified.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Monthly estimate strip */}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded border border-emerald-200 bg-emerald-50 p-2">
                    <div className="text-[10px] uppercase text-emerald-700">Monthly income</div>
                    <div className="text-sm font-semibold text-emerald-900">
                      {formatUSD(insights.totals.monthlyIncomeEstimate)}
                    </div>
                  </div>
                  <div className="rounded border border-rose-200 bg-rose-50 p-2">
                    <div className="text-[10px] uppercase text-rose-700">Monthly expenses</div>
                    <div className="text-sm font-semibold text-rose-900">
                      {formatUSD(insights.totals.monthlyExpenseEstimate)}
                    </div>
                  </div>
                  <div className={
                    "rounded border p-2 " +
                    (insights.totals.netMonthlyEstimate >= 0
                      ? "border-blue-200 bg-blue-50"
                      : "border-amber-200 bg-amber-50")
                  }>
                    <div className={"text-[10px] uppercase " + (insights.totals.netMonthlyEstimate >= 0 ? "text-blue-700" : "text-amber-700")}>
                      Net (est.)
                    </div>
                    <div className={"text-sm font-semibold " + (insights.totals.netMonthlyEstimate >= 0 ? "text-blue-900" : "text-amber-900")}>
                      {formatUSD(insights.totals.netMonthlyEstimate)}
                    </div>
                  </div>
                </div>

                {/* Recurring income */}
                {insights.recurringIncome.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-emerald-900 mb-1">
                      Recurring income ({insights.recurringIncome.length})
                    </div>
                    <ul className="space-y-1">
                      {insights.recurringIncome.slice(0, 5).map((r, i) => (
                        <li key={`inc-${i}`} className="text-xs flex items-center justify-between gap-2 py-1 px-2 rounded bg-white/60 border border-emerald-100" title={r.confidenceLabel || ""}>
                          <span className="font-medium truncate">{r.canonicalVendor}</span>
                          <span className="flex items-center gap-2 shrink-0">
                            <Badge variant="secondary" className="text-[9px] uppercase">{r.cadence}</Badge>
                            <span className="text-muted-foreground">~{formatUSD(r.avgAmount)}/{r.cadence === "monthly" ? "mo" : r.cadence === "biweekly" ? "2wk" : "wk"}</span>
                            {r.monthlyEstimateReliable === false ? (
                              <span className="text-muted-foreground italic text-[10px]">{r.monthlyEstimateLabel}</span>
                            ) : (
                              <span className="text-emerald-700">{formatUSD(r.monthlyEstimate)}/mo</span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Recurring expenses */}
                {insights.recurringExpenses.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-rose-900 mb-1">
                      Recurring expenses ({insights.recurringExpenses.length})
                    </div>
                    <ul className="space-y-1">
                      {insights.recurringExpenses.slice(0, 5).map((r, i) => (
                        <li key={`exp-${i}`} className="text-xs flex items-center justify-between gap-2 py-1 px-2 rounded bg-white/60 border border-rose-100" title={r.confidenceLabel || ""}>
                          <span className="font-medium truncate">{r.canonicalVendor}</span>
                          <span className="flex items-center gap-2 shrink-0">
                            <Badge variant="secondary" className="text-[9px] uppercase">{r.cadence}</Badge>
                            <span className="text-muted-foreground">~{formatUSD(r.avgAmount)}/{r.cadence === "monthly" ? "mo" : r.cadence === "biweekly" ? "2wk" : "wk"}</span>
                            {r.monthlyEstimateReliable === false ? (
                              <span className="text-muted-foreground italic text-[10px]">{r.monthlyEstimateLabel}</span>
                            ) : (
                              <span className="text-rose-700">{formatUSD(r.monthlyEstimate)}/mo</span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Anomalies */}
                {insights.anomalies.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-amber-900 mb-1 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Unusual transactions ({insights.anomalies.length})
                    </div>
                    <ul className="space-y-1">
                      {insights.anomalies.slice(0, 5).map((a, i) => (
                        <li key={`anom-${i}`} className="text-xs flex items-center justify-between gap-2 py-1 px-2 rounded bg-amber-50 border border-amber-200">
                          <span className="font-medium truncate">{a.description || a.canonicalVendor}</span>
                          <span className="flex items-center gap-2 shrink-0">
                            <span className="text-muted-foreground">{format(new Date(a.date), "MMM d")}</span>
                            <span className="text-amber-700">
                              {formatUSD(a.amount)} ({a.deviationPct > 0 ? "+" : ""}{a.deviationPct.toFixed(0)}%)
                            </span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Based on your past activity — moved to bottom per UX spec.
              Rewritten from "AI memory · X patterns learned" to client-
              friendly text. Raw confidence percentage replaced with date. */}
          {patternsResp?.patterns && patternsResp.patterns.length > 0 && (
            <Card className="bg-slate-50 border-slate-200">
              <CardContent className="py-2.5 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <Sparkles className="h-3.5 w-3.5 text-slate-500" />
                  <span>
                    Based on your past activity ({(() => {
                      const canonicalCount = new Set(
                        patternsResp.patterns
                          .map((p) => (p.canonicalVendor || p.vendorName || "").trim())
                          .filter(Boolean),
                      ).size || patternsResp.patterns.length;
                      return `${canonicalCount} vendor${canonicalCount === 1 ? "" : "s"} tracked`;
                    })()})
                  </span>
                </div>
                <span className="text-[11px] text-slate-500">
                  Last updated{" "}
                  {format(
                    new Date(
                      patternsResp.patterns
                        .map((p) => new Date(p.lastSeen).getTime())
                        .reduce((a, b) => Math.max(a, b), 0),
                    ),
                    "MMM d, yyyy",
                  )}
                </span>
              </CardContent>
            </Card>
          )}

          {/* AI Suggestions Banner */}
          {aiSuggestion && (
            <Card className={
              aiSuggestion.strategy === "all" || aiSuggestion.strategy === "balanced"
                ? "border-green-200 bg-green-50"
                : aiSuggestion.strategy === "partial"
                  ? "border-amber-200 bg-amber-50"
                  : "border-gray-200 bg-gray-50"
            }>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <Sparkles className={
                    "h-5 w-5 mt-0.5 shrink-0 " + (
                      aiSuggestion.strategy === "all" || aiSuggestion.strategy === "balanced"
                        ? "text-green-600"
                        : aiSuggestion.strategy === "partial"
                          ? "text-amber-600"
                          : "text-gray-500"
                    )
                  } />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-semibold">AI Reconciliation</h3>
                      <Badge variant="outline" className="text-[10px] uppercase">
                        {aiSuggestion.strategy === "all" && "Exact match"}
                        {aiSuggestion.strategy === "partial" && "Best-fit subset"}
                        {aiSuggestion.strategy === "manual" && "Needs your review"}
                        {aiSuggestion.strategy === "balanced" && "Balanced"}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{aiSuggestion.summary}</p>
                    {aiSuggestion.strategy === "partial" && aiSuggestion.matchedIds.size > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        disabled={clearTransactionsMutation.isPending}
                        onClick={() => clearTransactionsMutation.mutate(Array.from(aiSuggestion.matchedIds))}
                      >
                        <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                        Apply AI suggestion ({aiSuggestion.matchedIds.size} transactions)
                      </Button>
                    )}
                    {aiSuggestion.strategy === "all" && reconciliationData?.reconciliation?.status !== "completed" && (
                      <p className="text-[11px] text-green-700 mt-2">
                        Auto-applied. Review the cleared list, then click <strong>Complete Reconciliation</strong> below to lock this period.
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Reconciliation Progress Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Reconciliation Progress
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBulkClear}
                    disabled={selectedTransactions.size === 0 || clearTransactionsMutation.isPending}
                  >
                    Mark Selected as Cleared ({selectedTransactions.size})
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => autoMatchAllMutation.mutate()}
                    disabled={autoMatchAllMutation.isPending || (reconciliationData?.unclearedTransactions?.length ?? 0) === 0}
                    title="Mark every uncleared transaction as cleared"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    {autoMatchAllMutation.isPending
                      ? "Matching..."
                      : `Auto-Match (${reconciliationData?.unclearedTransactions?.length ?? 0})`}
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {reconciliationData && (
                <>
                {/* Editable balance row — Beginning + Bank Statement.
                    Disabled once the reconciliation is completed. */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end p-3 rounded-md bg-muted/40">
                  <div className="space-y-1">
                    <Label htmlFor="edit-beginning" className="text-xs">Beginning Balance</Label>
                    <Input
                      id="edit-beginning"
                      type="number"
                      step="0.01"
                      value={editBeginning}
                      onChange={(e) => setEditBeginning(e.target.value)}
                      disabled={reconciliationData.reconciliation?.status === "completed"}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="edit-bankstmt" className="text-xs">Bank Statement Balance</Label>
                    <Input
                      id="edit-bankstmt"
                      type="number"
                      step="0.01"
                      value={editBankStatement}
                      onChange={(e) => setEditBankStatement(e.target.value)}
                      disabled={reconciliationData.reconciliation?.status === "completed"}
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={
                      reconciliationData.reconciliation?.status === "completed" ||
                      updateBalancesMutation.isPending ||
                      (
                        Number(editBeginning || 0) === Number(reconciliationData.balances.beginning || 0) &&
                        Number(editBankStatement || 0) === Number(reconciliationData.balances.bankStatement || 0)
                      )
                    }
                    onClick={() => updateBalancesMutation.mutate({
                      beginningBalance: editBeginning,
                      bankStatementBalance: editBankStatement,
                    })}
                  >
                    {updateBalancesMutation.isPending ? "Saving..." : "Save Balances"}
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Beginning Balance</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatCurrency(reconciliationData.balances.beginning)}
                    </p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Bank Statement Balance</p>
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(reconciliationData.balances.bankStatement)}
                    </p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Book Balance</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {formatCurrency(reconciliationData.balances.bookBalance)}
                    </p>
                  </div>
                  <div className={`text-center p-4 rounded-lg ${
                    Math.abs(reconciliationData.balances.difference) < 0.01 
                      ? 'bg-green-50' 
                      : 'bg-red-50'
                  }`}>
                    <p className="text-sm text-muted-foreground">Difference</p>
                    <p className={`text-2xl font-bold ${
                      Math.abs(reconciliationData.balances.difference) < 0.01 
                        ? 'text-green-600' 
                        : 'text-red-600'
                    }`}>
                      {formatCurrency(reconciliationData.balances.difference)}
                    </p>
                  </div>
                </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Transactions Tabs */}
          <Tabs defaultValue="uncleared" className="space-y-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="uncleared">
                Needs Review ({reconciliationData?.unclearedTransactions.length || 0})
              </TabsTrigger>
              <TabsTrigger value="cleared">
                Auto-Matched ({reconciliationData?.clearedTransactions.length || 0})
              </TabsTrigger>
              <TabsTrigger value="discrepancies">
                Discrepancies ({reconciliationData?.discrepancies.length || 0})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="uncleared" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Needs Review</CardTitle>
                  <CardDescription>
                    Transactions the AI engine couldn't auto-match. Tick the ones that appear on your bank statement and click <em>Mark Selected as Cleared</em>, or use Auto-Match to clear them all at once.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {reconciliationData?.unclearedTransactions.map((transaction: any) => {
                      const key = extractVendorKey(transaction);
                      const pat = patternMap.get(key);
                      const anomaly = anomalyMap.get(transaction.id);
                      return (
                        <div
                          key={transaction.id}
                          className={`flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 ${anomaly ? "border-amber-300 bg-amber-50/30" : ""}`}
                        >
                          <Checkbox
                            checked={selectedTransactions.has(transaction.id)}
                            onCheckedChange={(checked) =>
                              handleTransactionSelection(transaction.id, checked as boolean)
                            }
                          />
                          <div className="flex-1">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium">{transaction.description}</p>
                                <p className="text-sm text-muted-foreground">
                                  {format(new Date(transaction.date), 'MMM dd, yyyy')}
                                </p>
                                {pat && (
                                  <div
                                    className="text-[11px] text-indigo-700 mt-1 flex items-center gap-1 flex-wrap"
                                    title={pat.explanation?.reason}
                                  >
                                    <Sparkles className="h-3 w-3" />
                                    <span>
                                      {pat.tentative
                                        ? `Possible recurring pattern · ${pat.occurrences}× seen so far`
                                        : `AI learned this pattern · ${pat.occurrences}× seen · ${humanConfidenceLabel(pat.weightedConfidence ?? pat.confidence).toLowerCase()}`}
                                    </span>
                                    {pat.cadence && pat.cadence !== "irregular" && (
                                      <Badge variant="secondary" className="text-[9px] uppercase ml-1">
                                        {pat.tentative ? "Possible" : "Recurring"} · {pat.cadence}
                                      </Badge>
                                    )}
                                    {pat.isOverdue && (
                                      <Badge variant="outline" className="text-[9px] uppercase ml-1 border-amber-400 text-amber-700">
                                        Overdue
                                      </Badge>
                                    )}
                                    {pat.explanation && (
                                      <details className="basis-full mt-1">
                                        <summary className="text-[10px] text-indigo-600 cursor-pointer hover:underline">
                                          AI Explanation
                                        </summary>
                                        <div className="text-[10px] text-muted-foreground mt-1 pl-4 border-l-2 border-indigo-200">
                                          <div>{pat.explanation.reason}</div>
                                          <div className="mt-0.5 text-[9px] uppercase tracking-wide opacity-75">
                                            factors · occ {pat.explanation.factors.occurrences} · avg ${pat.explanation.factors.avgAmount.toFixed(2)} · {pat.explanation.factors.cadence} · {pat.explanation.factors.recencyDays}d ago
                                          </div>
                                        </div>
                                      </details>
                                    )}
                                  </div>
                                )}
                                {anomaly && (
                                  <p className="text-[11px] text-amber-700 mt-1 flex items-center gap-1">
                                    <AlertTriangle className="h-3 w-3" />
                                    Flagged anomaly · {anomaly.reason}
                                  </p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className={`font-medium ${
                                  transaction.type === 'income' ? 'text-green-600' : 'text-red-600'
                                }`}>
                                  {transaction.type === 'income' ? '+' : '-'}
                                  {formatCurrency(Math.abs(parseFloat(transaction.amount)))}
                                </p>
                                <Badge variant="outline" className="text-xs">
                                  {transaction.type}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {(!reconciliationData?.unclearedTransactions.length) && (
                      <p className="text-center text-muted-foreground py-8">
                        No uncleared transactions found
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="cleared" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Auto-Matched</CardTitle>
                  <CardDescription>
                    Transactions cleared by the AI engine or manually. Confidence + reason shown per row.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {reconciliationData?.clearedTransactions.map((transaction: any) => {
                      // Pull AI confidence/reason if this row was part of the
                      // current suggestion. Manual clears get a neutral badge.
                      const aiMatch = aiSuggestion?.matches.find((m) => m.id === transaction.id && m.matched);
                      return (
                        <div
                          key={transaction.id}
                          className="flex items-center space-x-3 p-3 border rounded-lg bg-green-50"
                        >
                          <Check className="w-5 h-5 text-green-600" />
                          <div className="flex-1">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium">{transaction.description}</p>
                                <p className="text-sm text-muted-foreground">
                                  {format(new Date(transaction.date), 'MMM dd, yyyy')}
                                </p>
                                {aiMatch && (
                                  <p className="text-[11px] text-green-700 mt-1 flex items-center gap-1">
                                    <Sparkles className="h-3 w-3" />
                                    {aiMatch.reason} · {(aiMatch.confidence * 100).toFixed(0)}% confidence
                                  </p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className={`font-medium ${
                                  transaction.type === 'income' ? 'text-green-600' : 'text-red-600'
                                }`}>
                                  {transaction.type === 'income' ? '+' : '-'}
                                  {formatCurrency(Math.abs(parseFloat(transaction.amount)))}
                                </p>
                                <Badge variant="default" className={`text-xs ${aiMatch ? "bg-green-500" : "bg-gray-500"}`}>
                                  {aiMatch ? "AI cleared" : "Cleared"}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {(!reconciliationData?.clearedTransactions.length) && (
                      <p className="text-center text-muted-foreground py-8">
                        No cleared transactions yet
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="discrepancies" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Discrepancies</CardTitle>
                  <CardDescription>
                    Issues found during the reconciliation process
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {reconciliationData?.discrepancies.map((discrepancy: any, index: number) => (
                      <div
                        key={index}
                        className="p-4 border rounded-lg bg-yellow-50 border-yellow-200"
                      >
                        <div className="flex items-start space-x-3">
                          <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                          <div className="flex-1">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-yellow-800">
                                  {discrepancy.issueType.replace('_', ' ')}
                                </p>
                                <p className="text-sm text-yellow-700 mt-1">
                                  {discrepancy.description}
                                </p>
                                <p className="text-sm text-yellow-600 mt-2 font-medium">
                                  Suggested Fix: {discrepancy.suggestedFix}
                                </p>
                              </div>
                              {discrepancy.bankAmount && (
                                <Badge variant="outline" className="ml-2">
                                  {formatCurrency(parseFloat(discrepancy.bankAmount))}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {(!reconciliationData?.discrepancies.length) && (
                      <p className="text-center text-muted-foreground py-8">
                        No discrepancies found
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Complete Reconciliation */}
          {reconciliationData && Math.abs(reconciliationData.balances.difference) < 0.01 && (
            <Card className="border-green-200 bg-green-50">
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <Check className="w-12 h-12 text-green-600 mx-auto" />
                  <div>
                    <h3 className="text-lg font-semibold text-green-800">
                      Reconciliation complete
                    </h3>
                    <p className="text-green-700">
                      Your book balance matches the bank statement. Click below to lock this period.
                    </p>
                  </div>
                  <Button
                    onClick={() => completeReconciliationMutation.mutate()}
                    disabled={completeReconciliationMutation.isPending}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {completeReconciliationMutation.isPending ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Complete Reconciliation
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Reconciliation History */}
      {reconciliationHistory && reconciliationHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Reconciliation History</CardTitle>
            <CardDescription>
              Past reconciliations for the selected account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {reconciliationHistory.map((reconciliation: any) => (
                <div
                  key={reconciliation.id}
                  className="flex flex-col gap-2 p-3 border rounded-lg sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <p className="font-medium">
                      {format(new Date(reconciliation.startDate), 'MMM dd')} - {format(new Date(reconciliation.endDate), 'MMM dd, yyyy')}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Ending Balance: {formatCurrency(parseFloat(reconciliation.endingBalance || '0'))}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 sm:items-end">
                    <div className="flex items-center space-x-2">
                      {getStatusBadge(reconciliation.status)}
                      {reconciliation.reconciledAt && (
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(reconciliation.reconciledAt), 'MMM dd, yyyy')}
                        </p>
                      )}
                    </div>
                    {/* Lifecycle actions — Complete + Delete only when the
                        reconciliation isn't already locked. Resume always
                        available. Tenant safety enforced by the backend. */}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleResume(reconciliation.id)}
                      >
                        Resume
                      </Button>
                      {reconciliation.status !== "completed" && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleComplete(reconciliation.id)}
                        >
                          Complete
                        </Button>
                      )}
                      {reconciliation.status !== "completed" && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(reconciliation.id)}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      </div>
    </Layout>
  );
}
